import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Divider,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { 
  CloudUpload as UploadIcon,
  Article as DocumentIcon,
  Chat as ChatIcon,
  Download as DownloadIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import WorkflowChat from './WorkflowChat';
import useDocuments from '../../hooks/useDocuments';
import useNotification from '../../hooks/useNotification';

/**
 * Template component for document types in the PRD workflow
 * 
 * This serves as a base template that all document type components should follow
 * for consistency across the application.
 */
const DocumentTypeTemplate = ({
  projectId,
  documentType,
  title,
  icon: Icon,
  existingContent = '',
  onSave,
  renderPreview,
  promptConfig = {},
  allowedImportTypes = 'application/json,text/plain,text/markdown',
  useSplitView = false,
  customHandlers = {},
  children
}) => {
  // State
  const [activeTab, setActiveTab] = useState(0);
  const [content, setContent] = useState(existingContent);
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadContent, setUploadContent] = useState('');
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showChat, setShowChat] = useState(false);
  
  // Hooks
  const { showNotification } = useNotification();
  const { createDocument, getDocumentsByProjectAndType, getDocumentById } = useDocuments();
  
  // Load existing documents of this type
  useEffect(() => {
    if (projectId && documentType) {
      const fetchDocuments = async () => {
        try {
          const docs = await getDocumentsByProjectAndType(projectId, documentType);
          setExistingDocuments(docs || []);
        } catch (error) {
          console.error(`Error fetching ${documentType} documents:`, error);
        }
      };
      
      fetchDocuments();
    }
  }, [projectId, documentType, getDocumentsByProjectAndType]);
  
  // Effect to parse existing content if available
  useEffect(() => {
    if (existingContent) {
      setContent(existingContent);
    }
  }, [existingContent]);
  
  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Save document handler
  const saveDocument = async () => {
    try {
      setLoading(true);
      
      // Save to backend
      if (projectId) {
        const document = await createDocument(projectId, {
          title: title || documentType,
          content: content,
          type: documentType
        });
        
        showNotification({
          message: `${title} saved successfully`,
          type: 'success'
        });
        
        // Notify parent component
        if (onSave) {
          onSave(content);
        }
      }
    } catch (error) {
      console.error(`Error saving ${documentType}:`, error);
      showNotification({
        message: `Error saving ${title}: ${error.message}`,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle document import
  const handleUploadDocument = () => {
    if (!uploadContent.trim()) {
      showNotification({
        message: 'Please enter content to upload',
        type: 'warning'
      });
      return;
    }
    
    try {
      // Set the content
      setContent(uploadContent);
      setUploadDialogOpen(false);
      
      showNotification({
        message: `${title} content imported successfully`,
        type: 'success'
      });
    } catch (error) {
      console.error(`Error uploading ${documentType}:`, error);
      showNotification({
        message: `Error uploading ${title}: ${error.message}`,
        type: 'error'
      });
    }
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const fileContent = e.target.result;
      setUploadContent(fileContent);
    };
    
    if (file.type.includes('application/json') || 
        file.type.includes('text/plain') || 
        file.type.includes('text/markdown') ||
        file.type.includes('text/csv') || 
        file.name.endsWith('.md')) {
      reader.readAsText(file);
    } else {
      showNotification({
        message: `File type not supported: ${file.type}`,
        type: 'error'
      });
    }
  };

  // Load existing document
  const handleLoadExistingDocument = async (documentId) => {
    try {
      setLoading(true);
      const doc = await getDocumentById(documentId);
      if (doc && doc.content) {
        setContent(doc.content);
        setSelectedDocument(documentId);
        
        showNotification({
          message: `${title} loaded successfully`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error(`Error loading existing ${documentType}:`, error);
      showNotification({
        message: `Error loading document: ${error.message}`,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Export document
  const handleExportDocument = () => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `${documentType.toLowerCase().replace(/\s+/g, '-')}.md`;
    a.click();
    
    URL.revokeObjectURL(url);
    
    showNotification({
      message: `${title} exported successfully`,
      type: 'success'
    });
  };

  // Generate main section
  const renderMainSection = () => {
    return (
      <Box>
        {/* Three tiles section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Use Existing Tile */}
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={0} 
              variant="outlined" 
              sx={{ 
                p: 3, 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f8f9fa'
                }
              }}
              onClick={() => setActiveTab(1)}
            >
              <DocumentIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Use Existing
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select from previously created {title.toLowerCase()}
              </Typography>
              <Button 
                variant="outlined" 
                sx={{ mt: 2 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTab(1);
                }}
              >
                Browse Documents
              </Button>
            </Paper>
          </Grid>
          
          {/* Import Tile */}
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={0} 
              variant="outlined" 
              sx={{ 
                p: 3, 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f8f9fa'
                }
              }}
              onClick={() => setUploadDialogOpen(true)}
            >
              <UploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Import
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Import an existing {title.toLowerCase()} from a file
              </Typography>
              <Button 
                variant="outlined" 
                sx={{ mt: 2 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setUploadDialogOpen(true);
                }}
              >
                Upload File
              </Button>
            </Paper>
          </Grid>
          
          {/* Ask AI Tile */}
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={0} 
              variant="outlined" 
              sx={{ 
                p: 3, 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f8f9fa'
                }
              }}
              onClick={() => {
                // Use custom handler if provided, otherwise default behavior
                if (customHandlers.onChatButtonClick) {
                  customHandlers.onChatButtonClick();
                } else {
                  setShowChat(true);
                }
              }}
            >
              <ChatIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {promptConfig.tileTitle || "Ask AI"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Generate {title.toLowerCase()} with AI assistance
              </Typography>
              <Button 
                variant="outlined" 
                sx={{ mt: 2 }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Use custom handler if provided, otherwise default behavior
                  if (customHandlers.onChatButtonClick) {
                    customHandlers.onChatButtonClick();
                  } else {
                    setShowChat(true);
                  }
                }}
              >
                {promptConfig.buttonText || "Start Chat"}
              </Button>
            </Paper>
          </Grid>
        </Grid>
        
        {/* Preview/Content Area */}
        {content && (
          <Box>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{title} Preview</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    startIcon={<DownloadIcon />}
                    onClick={handleExportDocument}
                  >
                    Export
                  </Button>
                  <Button 
                    variant="contained" 
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={loading}
                    onClick={saveDocument}
                  >
                    Save {title}
                  </Button>
                </Box>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              {/* Preview content - either use the provided render function or default */}
              {renderPreview ? renderPreview(content) : (
                <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                  <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                    {content}
                  </pre>
                </Box>
              )}
            </Paper>
          </Box>
        )}
      </Box>
    );
  };

  // Render existing documents tab
  const renderExistingDocumentsTab = () => {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>Existing {title} Documents</Typography>
        <Divider sx={{ mb: 3 }} />
        
        {existingDocuments.length > 0 ? (
          <Grid container spacing={2}>
            {existingDocuments.map((doc) => (
              <Grid item xs={12} sm={6} md={4} key={doc._id}>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    cursor: 'pointer',
                    bgcolor: selectedDocument === doc._id ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                  onClick={() => handleLoadExistingDocument(doc._id)}
                >
                  <Typography variant="subtitle1" gutterBottom>{doc.title || title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Created: {new Date(doc.createdAt).toLocaleDateString()}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLoadExistingDocument(doc._id);
                    }}
                  >
                    Load Document
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <DocumentIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No existing {title.toLowerCase()} documents found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Create a new document using the AI or import options
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Render chat section
  const renderChatSection = () => {
    if (useSplitView) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <WorkflowChat
              sectionName={promptConfig.chatTitle || title}
              projectId={projectId}
              placeholder={promptConfig.placeholder || `Ask about ${title.toLowerCase()}...`}
              existingContent={content}
              promptConfig={promptConfig}
              onSaveContent={(newContent) => {
                setContent(newContent);
                if (onSave) {
                  onSave(newContent);
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" gutterBottom>Preview</Typography>
              <Divider sx={{ mb: 2 }} />
              {renderPreview ? renderPreview(content) : (
                <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, height: '500px', overflow: 'auto' }}>
                  <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                    {content}
                  </pre>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      );
    }
    
    return (
      <Box>
        <WorkflowChat
          sectionName={promptConfig.chatTitle || title}
          projectId={projectId}
          placeholder={promptConfig.placeholder || `Ask about ${title.toLowerCase()}...`}
          existingContent={content}
          promptConfig={promptConfig}
          onSaveContent={(newContent) => {
            setContent(newContent);
            if (onSave) {
              onSave(newContent);
            }
          }}
        />
      </Box>
    );
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">{title}</Typography>
        {content && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="contained" 
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={loading}
              onClick={saveDocument}
            >
              Save {title}
            </Button>
          </Box>
        )}
      </Box>

      {/* Main Tabs */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        <Tab label="Main" />
        <Tab label="Existing Documents" />
        {showChat && <Tab label="AI Chat" />}
        {children && <Tab label="Advanced" />}
      </Tabs>

      {/* Tab Content */}
      {activeTab === 0 && renderMainSection()}
      {activeTab === 1 && renderExistingDocumentsTab()}
      {activeTab === 2 && showChat && renderChatSection()}
      {activeTab === 3 && children && (
        <Box>
          {children}
        </Box>
      )}
      
      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import {title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 2 }}>
            Paste your content below or upload a file.
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <input
              type="file"
              id="file-upload"
              style={{ display: 'none' }}
              accept={allowedImportTypes}
              onChange={handleFileUpload}
            />
            <label htmlFor="file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
              >
                Upload File
              </Button>
            </label>
          </Box>
          
          <TextField
            fullWidth
            multiline
            rows={12}
            placeholder={`Paste your ${title.toLowerCase()} content here...`}
            value={uploadContent}
            onChange={(e) => setUploadContent(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUploadDocument}>Import</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DocumentTypeTemplate;